import { useCallback, useEffect, useMemo, useState } from "react";
import CommunicationMessageComposer from "../communication/CommunicationMessageComposer";
import CommunicationThreadDetail from "../communication/CommunicationThreadDetail";
import CommunicationThreadList from "../communication/CommunicationThreadList";
import {
  createCommunicationMessage,
  createCommunicationThread,
  getCommunicationThread,
  linkCommunicationThreadToProject,
  listCommunicationThreads,
  markCommunicationThreadRead,
  sendCommunicationThreadPortalNotification,
  updateCommunicationThreadPortalVisibility,
  updateCommunicationThreadReplyPolicy,
  updateCommunicationThreadStatus,
} from "../../services/communicationService";

function getErrorMessage(error, fallback) {
  return (
    error?.apiError?.message ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

export default function ClientCommunicationTab({ client, data }) {
  const [threads, setThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState("");
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [statusValue, setStatusValue] = useState("new");
  const [statusSaving, setStatusSaving] = useState(false);
  const [projectLinkValue, setProjectLinkValue] = useState("");
  const [projectLinkSaving, setProjectLinkSaving] = useState(false);
  const [messageSaving, setMessageSaving] = useState(false);
  const [threadSaving, setThreadSaving] = useState(false);
  const [markReadSaving, setMarkReadSaving] = useState(false);
  const [portalVisibilitySaving, setPortalVisibilitySaving] = useState(false);
  const [portalReplyPolicySaving, setPortalReplyPolicySaving] = useState(false);
  const [portalNotificationSending, setPortalNotificationSending] = useState(false);

  const relatedProjects = useMemo(
    () => (Array.isArray(data?.projects) ? data.projects : []),
    [data?.projects]
  );

  const loadThreads = useCallback(async () => {
    if (!client?.id) return;
    try {
      setLoadingList(true);
      setError("");
      const nextThreads = await listCommunicationThreads({
        clientId: client.id,
        context: "client",
      });
      setThreads(nextThreads);
      setSelectedThreadId((current) => {
        if (current && nextThreads.some((thread) => String(thread._id) === String(current))) {
          return current;
        }
        return String(nextThreads[0]?._id || "");
      });
    } catch (loadError) {
      setError(getErrorMessage(loadError, "Klantcommunicatie laden mislukt."));
      setThreads([]);
      setSelectedThreadId("");
    } finally {
      setLoadingList(false);
    }
  }, [client?.id]);

  const loadThreadDetail = useCallback(async (threadId) => {
    if (!threadId) {
      setSelectedThread(null);
      setMessages([]);
      setStatusValue("new");
      setProjectLinkValue("");
      return;
    }

    try {
      setLoadingDetail(true);
      setDetailError("");
      const detail = await getCommunicationThread(threadId, { context: "client" });
      setSelectedThread(detail.thread);
      setMessages(detail.messages);
      setStatusValue(detail.thread?.status || "new");
      setProjectLinkValue(String(detail.thread?.projectId?._id || detail.thread?.projectId || ""));
      if (detail?.meta?.attentionCleared) {
        await loadThreads();
      }
    } catch (loadError) {
      setSelectedThread(null);
      setMessages([]);
      setDetailError(getErrorMessage(loadError, "Communicatiedetail laden mislukt."));
    } finally {
      setLoadingDetail(false);
    }
  }, [loadThreads]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    loadThreadDetail(selectedThreadId);
  }, [loadThreadDetail, selectedThreadId]);

  async function handleCreateThread(payload) {
    try {
      setThreadSaving(true);
      setError("");
      const participants = payload?.from?.name || payload?.from?.email
        ? [
            {
              name: payload.from.name,
              email: payload.from.email,
              type: "customer",
            },
          ]
        : [];
      const projectId = relatedProjects.length === 1 ? relatedProjects[0].id : null;

      const created = await createCommunicationThread({
        clientId: client.id,
        projectId,
        subject: payload.subject,
        status: "action_needed",
        participants,
        initialMessage: {
          direction: payload.direction,
          from: payload.from,
          bodyText: payload.bodyText,
        },
      });

      await loadThreads();
      const nextId = String(created?.thread?._id || "");
      setSelectedThreadId(nextId);
      if (nextId) {
        await loadThreadDetail(nextId);
      }
    } catch (saveError) {
      setError(getErrorMessage(saveError, "Nieuw gesprek aanmaken mislukt."));
      throw saveError;
    } finally {
      setThreadSaving(false);
    }
  }

  async function handleAddMessage(payload) {
    if (!selectedThreadId) return;
    try {
      setMessageSaving(true);
      setDetailError("");
      await createCommunicationMessage(selectedThreadId, {
        direction: payload.direction,
        from: payload.from,
        bodyText: payload.bodyText,
      });
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Bericht toevoegen mislukt."));
      throw saveError;
    } finally {
      setMessageSaving(false);
    }
  }

  async function handleSaveStatus() {
    if (!selectedThreadId) return;
    try {
      setStatusSaving(true);
      setDetailError("");
      await updateCommunicationThreadStatus(selectedThreadId, statusValue);
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Status wijzigen mislukt."));
    } finally {
      setStatusSaving(false);
    }
  }

  async function handleLinkProject() {
    if (!selectedThreadId || !projectLinkValue) return;
    try {
      setProjectLinkSaving(true);
      setDetailError("");
      await linkCommunicationThreadToProject(selectedThreadId, projectLinkValue);
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Project koppelen mislukt."));
    } finally {
      setProjectLinkSaving(false);
    }
  }

  async function handleMarkRead() {
    if (!selectedThreadId) return;
    try {
      setMarkReadSaving(true);
      setDetailError("");
      await markCommunicationThreadRead(selectedThreadId, { context: "client" });
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Markeren als gelezen mislukt."));
    } finally {
      setMarkReadSaving(false);
    }
  }

  async function handlePortalVisibilityToggle(nextVisible) {
    if (!selectedThreadId) return;
    try {
      setPortalVisibilitySaving(true);
      setDetailError("");
      await updateCommunicationThreadPortalVisibility(selectedThreadId, Boolean(nextVisible), {
        context: "client",
      });
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Klantportaal-zichtbaarheid wijzigen mislukt."));
    } finally {
      setPortalVisibilitySaving(false);
    }
  }

  async function handlePortalReplyPolicyToggle(nextAllowReply) {
    if (!selectedThreadId) return;
    try {
      setPortalReplyPolicySaving(true);
      setDetailError("");
      await updateCommunicationThreadReplyPolicy(selectedThreadId, Boolean(nextAllowReply), {
        context: "client",
      });
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Klantportaal reactiebeleid wijzigen mislukt."));
    } finally {
      setPortalReplyPolicySaving(false);
    }
  }

  async function handlePortalNotificationSend() {
    if (!selectedThreadId) return;
    try {
      setPortalNotificationSending(true);
      setDetailError("");
      await sendCommunicationThreadPortalNotification(selectedThreadId, {
        context: "client",
      });
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Klantportaal notificatiemail versturen mislukt."));
    } finally {
      setPortalNotificationSending(false);
    }
  }

  return (
    <section className="space-y-5">
      {error ? <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{error}</div> : null}

      <CommunicationMessageComposer
        title="Nieuw gesprek"
        includeSubject
        submitLabel="Gesprek aanmaken"
        disabled={threadSaving}
        onSubmit={handleCreateThread}
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <CommunicationThreadList
          threads={threads}
          selectedThreadId={selectedThreadId}
          onSelect={(thread) => setSelectedThreadId(String(thread?._id || ""))}
          loading={loadingList}
          emptyText="Nog geen klantcommunicatie vastgelegd."
        />

        <CommunicationThreadDetail
          thread={selectedThread}
          messages={messages}
          loading={loadingDetail}
          detailError={detailError}
          statusValue={statusValue}
          onStatusChange={setStatusValue}
          onStatusSave={handleSaveStatus}
          statusSaving={statusSaving}
          projectLinkValue={projectLinkValue}
          onProjectLinkChange={setProjectLinkValue}
          onProjectLinkSave={handleLinkProject}
          projectOptions={relatedProjects}
          projectLinkSaving={projectLinkSaving}
          canLinkProject={relatedProjects.length > 0}
          messageSaving={messageSaving}
          onSubmitMessage={handleAddMessage}
          markReadSaving={markReadSaving}
          onMarkRead={handleMarkRead}
          portalVisibilitySaving={portalVisibilitySaving}
          onPortalVisibilityToggle={handlePortalVisibilityToggle}
          portalReplyPolicySaving={portalReplyPolicySaving}
          onPortalReplyPolicyToggle={handlePortalReplyPolicyToggle}
          portalNotificationSending={portalNotificationSending}
          onPortalNotificationSend={handlePortalNotificationSend}
        />
      </div>
    </section>
  );
}
